/**
 * SubNav component is use to show the navigation bar with menus
 * selectedTab property is required to select the active menu
 * action property is required to handle menu click which need to be handled in the parent component
 * tabs property will have menu labels and active status.
 */

import React, { useState, useEffect, useCallback } from "react";
import fordStyles from "./SubNav.module.scss";
import lincolnStyles from "./SubNav-Lincoln.module.scss";
import commonFordStyles from "./../../styles/common/CommonStyles.module.scss";
import commonLincolnStyles from "./../../styles/common/CommonStyles-Lincoln.module.scss";
import SubNavDropdown from "./SubNavDropdown";
import HasPermissionTo from "../../common/HasPermissionTo";
import SubNavTabs from "./SubNavTabs";
import { withTranslation } from "react-i18next";
import CustomDropdown from "./CustomDropdown";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import UpperCaseText from "../../utils/UpperCaseText";

function SubNav(props) {
  const [visibleTabsCount, setVisibleTabsCount] = useState(100);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const tabletWindowWidth = 1024;

  const onboardedDealers =
    UpperCaseText(props.user?.userType) === "DEALER" &&
    props.user?.getOnboardedDealerGroup &&
    typeof props.user.getOnboardedDealerGroup === "function"
      ? props.user.getOnboardedDealerGroup()
      : [];

  const inViewPort = useCallback((boundary, divPaddingRight, windowWidth) => {
    return boundary.left >= 0 && boundary.right + divPaddingRight < windowWidth;
  }, []);

  const getVisibleTabsCount = useCallback(() => {
    if (windowWidth > tabletWindowWidth) {
      let visibleTabs = 0;
      let notVisibleTabsFlag = false;
      let elemRightBoundary = 0;
      const divPaddingRightPercent = 6;
      const divPaddingRight = windowWidth * (divPaddingRightPercent / 100);
      const moreTabWidth = 172;

      props.tabs?.forEach((item) => {
        if (!notVisibleTabsFlag) {
          const elem = document.getElementById("tab" + item.id);
          if (elem) {
            const boundary = elem.getBoundingClientRect();
            if (inViewPort(boundary, divPaddingRight, windowWidth)) {
              visibleTabs = visibleTabs + 1;
            } else {
              // checking if there is enough space for More Tab. Otherwise, reduce one Visible Tab to make space.
              if (
                elemRightBoundary + divPaddingRight + moreTabWidth >=
                windowWidth
              ) {
                visibleTabs = visibleTabs - 1;
              }
              notVisibleTabsFlag = true;
            }
            elemRightBoundary = boundary.right;
          }
        }
      });

      setVisibleTabsCount(visibleTabs);
    }
  }, [windowWidth, tabletWindowWidth, props.tabs, inViewPort]);

  const handleWindowResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
    setVisibleTabsCount(100);
    getVisibleTabsCount();
  }, [getVisibleTabsCount]);

  useEffect(() => {
    if (props.tabs) {
      window.addEventListener("resize", handleWindowResize);
      getVisibleTabsCount();

      return () => {
        window.removeEventListener("resize", handleWindowResize);
      };
    }
  }, [props.tabs, handleWindowResize, getVisibleTabsCount]);

  const isFordBrand = IS_FORD_BRAND(props.user?.brand);
  const styles = isFordBrand ? fordStyles : lincolnStyles;
  const commonStyles = isFordBrand ? commonFordStyles : commonLincolnStyles;

  return (
    <div className={styles.subNav}>
      <div>
        <div className={styles.container}>
          {props.dashboard && props.user?.fullName ? (
            <p id="welcome" className={styles.welcome}>
              {props.t("SubNav.welcome")}{" "}
              {props.user.fullName.split(" ")[0]}
            </p>
          ) : (
            ""
          )}
          <div className={styles.titleDiv}>
            <div className={commonStyles.pageTitle}>{props.title}</div>
            {!props.restrictDropdown ? (
              props.showCustomDropdown ? (
                <CustomDropdown {...props} isFordBrand={isFordBrand} />
              ) : (
                <HasPermissionTo
                  perform={["AllCMA:view"]}
                  permissions={props.user?.permissions?.rolePermissions}
                  condition={onboardedDealers && onboardedDealers.length > 1}
                  render={() => (
                    <SubNavDropdown
                      user={props.user}
                      onDealerChanged={props.onDealerChanged}
                      t={props.t}
                      userDetails={props.userDetails}
                    />
                  )}
                />
              )
            ) : null}
          </div>
        </div>
        {props.renderSubHeader && props.subHeader()}

        <SubNavTabs
          visibleTabsCount={visibleTabsCount}
          isFordBrand={isFordBrand}
          {...props}
        />
      </div>
    </div>
  );
}

export default withTranslation("emp")(SubNav);
